import AdminsApiV2 from 'api/v2/admins-api-v2.js'
import InputField from 'components/molecules/input-field'
import PublicHeader from 'components/organisms/public-header'
import PublicPageTemplate from 'components/templates/public-page-template'
import { Form, Formik } from 'formik'
import { useAuth } from 'providers/auth-provider'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { Button, Card, CardBody } from 'reactstrap'
import loginSchema from './login-schema'
import { LOCAL_STORAGE_JWT_TOKEN } from 'domains/admin/constants'

const SAVED_ADMIN_ACCOUNTS_KEY = 'BNK_SAVED_ADMIN_ACCOUNTS'
const MAX_SAVED_ADMIN_ACCOUNTS = 8

const readSavedAdminAccounts = () => {
  try {
    const accounts = JSON.parse(localStorage.getItem(SAVED_ADMIN_ACCOUNTS_KEY) || '[]')
    return Array.isArray(accounts) ? accounts.filter((account) => account && account.email) : []
  } catch (_) {
    return []
  }
}

const saveAdminAccount = (admin = {}) => {
  const email = String(admin.email || '').trim().toLowerCase()
  if (!email) return []

  const name = [admin.first_name, admin.last_name].filter(Boolean).join(' ').trim() || email
  const account = {
    email,
    name,
    role: admin.access || admin.role || '',
    lastLoginAt: new Date().toISOString()
  }

  const rest = readSavedAdminAccounts().filter((item) => String(item.email).toLowerCase() !== email)
  const next = [account, ...rest].slice(0, MAX_SAVED_ADMIN_ACCOUNTS)
  localStorage.setItem(SAVED_ADMIN_ACCOUNTS_KEY, JSON.stringify(next))
  return next
}

const removeSavedAdminAccount = (email) => {
  const normalizedEmail = String(email || '').trim().toLowerCase()
  const next = readSavedAdminAccounts().filter((item) => String(item.email).toLowerCase() !== normalizedEmail)
  localStorage.setItem(SAVED_ADMIN_ACCOUNTS_KEY, JSON.stringify(next))
  return next
}

const LoginPage = () => {
  useEffect(() => {
    return () => {
      document.body.classList.remove('bg-default')
    }
  }, [])

  const authContext = useAuth()
  const history     = useHistory()
  const [ savedAccounts, setSavedAccounts ] = useState(readSavedAdminAccounts)

  useEffect(() => {
    if (authContext.isAuth) history.replace('/app')
  }, [authContext.isAuth, history])

  const handleSubmit = async (values, actions) => {
    try {
      const response = await AdminsApiV2.login(values.email, values.password)
      if (response?.data?.token) {
        localStorage.setItem(LOCAL_STORAGE_JWT_TOKEN, response.data.token)
      }
      const admin = response.data.admin
      setSavedAccounts(saveAdminAccount(admin))

      authContext.onLogin({
        name  : `${admin.first_name} ${admin.last_name}`,
        role  : admin.access || admin.role || '',
        email : admin.email || values.email
      })
    } catch (error) {
      actions.setSubmitting(false)
      alert(error.response?.data?.error || error.message)
    }
  }

  return (
    <PublicPageTemplate
      header={<PublicHeader title='Belle and Kate' lead='Dashboard'/>}
    >
      <Card className="bg-secondary border-0 mb-0">
        <CardBody className="px-lg-5 py-lg-5">
          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
          >
            {(formikProps) => (
              <Form autoComplete='on'>
                {savedAccounts.length > 0 && (
                  <div className='mb-4'>
                    <div className='text-muted small mb-2'>Saved accounts</div>
                    {savedAccounts.map((account) => (
                      <div
                        key={account.email}
                        className='d-flex align-items-center justify-content-between bg-white rounded border px-3 py-2 mb-2'
                        style={{ gap: 12 }}
                      >
                        <button
                          type='button'
                          className='btn btn-link text-left p-0 flex-grow-1'
                          onClick={() => {
                            formikProps.setFieldValue('email', account.email)
                            setTimeout(() => {
                              const passwordInput = document.querySelector('input[name="password"]')
                              passwordInput && passwordInput.focus()
                            }, 0)
                          }}
                          title='Use this account'
                        >
                          <div className='font-weight-bold'>{account.name || account.email}</div>
                          <div className='small text-muted'>{account.email}</div>
                        </button>
                        <button
                          type='button'
                          className='btn btn-link text-danger p-0'
                          onClick={() => setSavedAccounts(removeSavedAdminAccount(account.email))}
                          title='Remove saved account'
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div className='small text-muted'>Klik email untuk isi account. Password tetap aman lewat browser password manager.</div>
                  </div>
                )}
                <InputField
                  type='email'
                  name='email'
                  placeholder='Email'
                  autoComplete='username'
                  inputMode='email'
                />
                <InputField
                  type='password'
                  name='password'
                  placeholder='Password'
                  autoComplete='current-password'
                />
                <Button
                  block
                  color='success'
                  type='submit'
                  disabled={formikProps.isSubmitting}
                >
                  Login
                </Button>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </PublicPageTemplate>
  )
}

export default LoginPage
